// MUST be the first import: it sets `__webpack_public_path__` from the bundle's serving origin
// before any asset-importing module is evaluated (see setPublicPath.js for why ordering matters).
import { BUNDLE_ORIGIN } from './setPublicPath';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { StyleSheetManager } from 'styled-components';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { PreloadAllFontsBase64, preloadMaterialTextFonts } from 'pxc-editor-material-svg-rendering/components/InteractiveRenderer/FontsLoader';
import { MARKING_EDITOR_WEB_COMPONENT_ELEMENT } from '@/constants';
import { changeLocale } from '@/actions/translations';
import { getDataFromAttributesThunk } from '@/store/actions/cmme';
import { inlcudePolyfills } from '@/helpers/polyfill';
import { loadRuntimeConfig } from '@/config/runtimeConfig';
import store from './store';
import { WebComponentGlobalStyles, MuiButton } from './globalStyles';
import { App } from './components';
import LatoRangeOne from './assets/fonts/lato-range-one.woff2';
import LatoRangeTwo from './assets/fonts/lato-range-two.woff2';
import RobotoCyrillicExt from './assets/fonts/roboto-cyrillic-ext.woff2';
import RobotoCyrillic from './assets/fonts/roboto-cyrillic.woff2';
import RobotoGreekExt from './assets/fonts/roboto-greek-ext.woff2';
import RobotoGreek from './assets/fonts/roboto-greek.woff2';
import RobotoVietnamese from './assets/fonts/roboto-vietnamese.woff2';
import RobotoLatinExt from './assets/fonts/roboto-latin-ext.woff2';
import RobotoLatin from './assets/fonts/roboto-latin.woff2';
import IBMPlexSans from './assets/fonts/IBMPlexSans-Regular.woff2';
import IBMPlexSansBold from './assets/fonts/IBMPlexSans-Bold.woff2';
import IBMPlexSansRegular from './assets/fonts/ibmplex/IBMPlexSans-Regular.ttf';
import IBMPlexSansSemiBold from './assets/fonts/ibmplex/IBMPlexSans-SemiBold.ttf';
import SourceSansPro from './assets/fonts/SourceSansPro-Regular.ttf.woff2';
import SourceSansProBold from './assets/fonts/SourceSansPro-Semibold.ttf.woff2';
import './web-component.css';

inlcudePolyfills();

const AppWrap = ({ data }) => {
  if (data?.locale) {
    const [code] = data.locale.split('-');

    store.dispatch(changeLocale(code));
    store.dispatch(getDataFromAttributesThunk(data));
  }

  return (
    <Provider store={store}>
      <WebComponentGlobalStyles />
      <App />
    </Provider>
  );
};

class MarkingEditorWebComponent extends HTMLElement {
  data = null;

  constructor() {
    super();
    const searchParams = new URLSearchParams(location.search);
    const _realm = searchParams.get('_realm');
    const _locale = searchParams.get('_locale');
    const productId = searchParams.get('productId');
    const locale = this.getAttribute('locale') || _locale;
    const [code1, code2] = locale.split('-');
    const realm = this.getAttribute('realm') || _realm || code2?.toLowerCase();
    const data = {
      locale: locale || '',
      realm: realm || '',
      productId: this.getAttribute('product-id') || productId || '',
      authToken: this.getAttribute('auth-token') || '',
    };
    this.data = data;
  }

  connectCssStyleSheets() {
    const fontFaceSheetContent = `
      @font-face {
        font-family: 'Lato';
        font-style: normal;
        font-weight: 400;
        src: url(${LatoRangeOne}) format('woff2');
        unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF;
      }

      @font-face {
        font-family: 'Lato';
        font-style: normal;
        font-weight: 400;
        src: url(${LatoRangeTwo}) format('woff2');
        unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
      }

      @font-face {
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 400;
        src: url(${RobotoCyrillicExt}) format('woff2');
        unicode-range: U+0460-052F, U+1C80-1C88, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F;
      }

      @font-face {
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 400;
        src: url(${RobotoCyrillic}) format('woff2');
        unicode-range: U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116;
      }

      @font-face {
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 400;
        src: url(${RobotoGreekExt}) format('woff2');
        unicode-range: U+1F00-1FFF;
      }

      @font-face {
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 400;
        src: url(${RobotoGreek}) format('woff2');
        unicode-range: U+0370-03FF;
      }

      @font-face {
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 400;
        src: url(${RobotoVietnamese}) format('woff2');
        unicode-range: U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+1EA0-1EF9, U+20AB;
      }

      @font-face {
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 400;
        src: url(${RobotoLatinExt}) format('woff2');
        unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF;
      }

      @font-face {
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 400;
        src: url(${RobotoLatin}) format('woff2');
        unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
      }

      @font-face {
        font-family: 'IBMPlexSans';
        font-style: normal;
        font-weight: 400;
        src: url(${IBMPlexSans}) format('woff2');
      }

      @font-face {
        font-family: 'IBMPlexSans';
        font-style: normal;
        font-weight: 400;
        src: url(${IBMPlexSansRegular}) format('truetype');
      }

      @font-face {
        font-family: 'IBMPlexSans';
        font-style: normal;
        font-weight: 500;
        src: url(${IBMPlexSansSemiBold}) format('truetype');
      }

      @font-face {
        font-family: 'IBMPlexSans';
        font-style: normal;
        font-weight: 700;
        src: url(${IBMPlexSansBold}) format('woff2');
      }

      @font-face {
        font-family: 'SourceSansPro';
        font-style: normal;
        font-weight: 400;
        src: url(${SourceSansPro}) format('woff2');
      }

      @font-face {
        font-family: 'SourceSansPro';
        font-style: normal;
        font-weight: 700;
        src: url(${SourceSansProBold}) format('woff2');
      }
    `;
    const htmlCssStyleSheetContent = `
      .full-height {
        overflow: hidden;
        padding: 0;
        margin: 0;
        width: 100%;
        height: 100%;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -o-user-select: none;
        user-select: none;
      }

      .full-height body {
        padding: 0;
        margin: 0;
        width: 100%;
        height: 100%;
        font-family: "Roboto", "Helvetica", "Arial", sans-serif;
      }
    `;

    try {
      const fontFaceSheet = new CSSStyleSheet();
      const htmlCssStyleSheet = new CSSStyleSheet();

      fontFaceSheet.replaceSync(fontFaceSheetContent);
      htmlCssStyleSheet.replaceSync(htmlCssStyleSheetContent);

      document.adoptedStyleSheets = [...document.adoptedStyleSheets, fontFaceSheet, htmlCssStyleSheet];
    } catch (error) {
      console.error('[connectCssStyleSheets]:', error);

      const fontFaceSheet = document.createElement('style');
      const htmlCssStyleSheet = document.createElement('style');

      fontFaceSheet.setAttribute('type', 'text/css');
      htmlCssStyleSheet.setAttribute('type', 'text/css');

      fontFaceSheet.innerText = fontFaceSheetContent;
      htmlCssStyleSheet.innerHTML = htmlCssStyleSheetContent;

      this.shadowRoot.appendChild(fontFaceSheet);

      document.head.appendChild(htmlCssStyleSheet);
    }
  }

  async connectedCallback() {
    const emotionRoot = document.createElement('style');
    const styledComponentsPoint = document.createElement('style');
    const shadowRootElement = document.createElement('div');

    shadowRootElement.style.position = 'relative';
    shadowRootElement.style.width = '100%';
    shadowRootElement.style.height = '100%';
    shadowRootElement.style.overflow = 'hidden';

    this.attachShadow({ mode: 'open' });

    this.shadowRoot.appendChild(styledComponentsPoint);
    this.shadowRoot.appendChild(emotionRoot);
    this.shadowRoot.appendChild(shadowRootElement);

    // Load runtime config from the bundle's own origin in parallel with the font preloads, so it
    // adds no extra serial round-trip. Awaited before ReactDOM.render so consumers read real values.
    await Promise.all([
      preloadMaterialTextFonts(),
      PreloadAllFontsBase64(),
      loadRuntimeConfig(BUNDLE_ORIGIN),
    ]);

    this.connectCssStyleSheets();

    const cache = createCache({
      key: 'css',
      prepend: true,
      container: emotionRoot,
    });

    const shadowTheme = createTheme({
      components: {
        MuiPopover: {
          defaultProps: {
            container: shadowRootElement,
          },
        },
        MuiPopper: {
          defaultProps: {
            container: shadowRootElement,
          },
        },
        MuiModal: {
          defaultProps: {
            container: shadowRootElement,
          },
        },
        ...MuiButton,
      },
    });

    ReactDOM.render(
      <CacheProvider value={cache}>
        <StyleSheetManager target={styledComponentsPoint}>
          <ThemeProvider theme={shadowTheme}>
            <AppWrap data={this.data} />
          </ThemeProvider>
        </StyleSheetManager>
      </CacheProvider>,
      shadowRootElement,
    );
  }

  onParameterChange() {}
}

window.customElements.define(MARKING_EDITOR_WEB_COMPONENT_ELEMENT, MarkingEditorWebComponent);
